import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import noImage from "../../../assets/images/common/importerr-no-image-logo.png";
import { TbZoomScan } from "react-icons/tb";
import { FaSpinner, FaExternalLinkAlt, FaStar, FaStore, FaBox, FaSync } from "react-icons/fa";
import { fetchToken } from "../../../lib/globalApiClient";
import "../amazonProduct/amazonProduct.css";

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';
const DEFAULT_PRICING_DOMAIN = 'importerr.com_common';

const getOnePieceRmbPrice = (item, fallback = 0) => {
  const value = parseFloat(
    item?.fenxiaoPriceInfo?.onePiecePrice ??
    item?.onePiecePrice ??
    item?.priceInfo?.onePiecePrice ??
    item?.priceInfo?.price ??
    item?.price ??
    item?.salePrice ??
    item?.retailPrice ??
    item?.ap ??
    0
  );
  if (Number.isFinite(value) && value > 0) return value;
  const fallbackValue = parseFloat(fallback || 0);
  return Number.isFinite(fallbackValue) && fallbackValue > 0 ? fallbackValue : 0;
};

export default function SellerFlipkartProductDetail({ url: urlProp }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlFromQuery = urlProp || searchParams.get("url") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("description");

  // Similar products state
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [, setPricingCache] = useState({});
  const pricingCacheRef = useRef({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortOption, setSortOption] = useState('default');
  const [findSimilarProgress, setFindSimilarProgress] = useState(0);
  const [findSimilarStatus, setFindSimilarStatus] = useState('');

  // Selected product detail state (inline in sidebar)
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [selectedProductVariants, setSelectedProductVariants] = useState([]);
  const [loadingSelectedProduct, setLoadingSelectedProduct] = useState(false);
  const [selectedVariantImageIdx, setSelectedVariantImageIdx] = useState(0);

  const sortedSimilarProducts = useMemo(() => {
    if (!similarProducts || similarProducts.length === 0) return [];
    switch (sortOption) {
      case 'price-low':
        return [...similarProducts].sort((a, b) => (a.price?.inr || 0) - (b.price?.inr || 0));
      case 'price-high':
        return [...similarProducts].sort((a, b) => (b.price?.inr || 0) - (a.price?.inr || 0));
      default:
        return similarProducts;
    }
  }, [similarProducts, sortOption]);

  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '', skuId = '', weight = 0, dimensions = null) => {
    if (!rmbPrice || rmbPrice <= 0) return { inr: 0, breakdown: null };
    let volumetricWeight = 0;
    if (dimensions) {
      const { length = 0, width = 0, height = 0 } = dimensions;
      if (length > 0 && width > 0 && height > 0) volumetricWeight = (length * width * height) / 5000;
    }
    const deadWeight = weight || 0.5;
    const cacheKey = `${rmbPrice.toFixed(2)}_${offerId}_${skuId}_${DEFAULT_PRICING_DOMAIN}`;
    if (pricingCacheRef.current[cacheKey]) return pricingCacheRef.current[cacheKey];
    try {
      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ap: parseFloat(rmbPrice), ls: 0, hsn: false, hsnTaxPercent: 18, dw: deadWeight, vw: volumetricWeight, sku: String(offerId || ''), testDomain: DEFAULT_PRICING_DOMAIN })
      });
      if (!response.ok) return { inr: 0, breakdown: null };
      const result = await response.json();
      const inrPrice = parseFloat(result?.data?.breakdown?.FBP || result?.breakdown?.FBP || 0);
      const breakdown = result?.data?.breakdown || result?.breakdown || null;
      const priceData = { inr: inrPrice, breakdown, deadWeight, volumetricWeight, dimensions };
      pricingCacheRef.current[cacheKey] = priceData;
      setPricingCache(prev => ({ ...prev, [cacheKey]: priceData }));
      return priceData;
    } catch { return { inr: 0, breakdown: null }; }
  }, []);

  const fetchProductDetailsWithVariants = useCallback(async (searchResultItem) => {
    const makeBasic = (item) => ({
      id: item.offerId || item.id || `p-${Math.random()}`,
      offerId: item.offerId || item.id || '',
      title: item.subjectTrans || item.subject || item.title || 'Product',
      image: item.imageUrl || item.image || '',
      price: { rmb: getOnePieceRmbPrice(item, 0), inr: 0 },
      variantCount: 1,
      supplier: item.companyName || item.supplier || 'Supplier',
      monthSold: item.monthSold || 0,
      moq: item.moq || item.minOrderQuantity || 1,
      rating: item.sellerRating || item.rating || null,
    });
    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const offerId = searchResultItem.offerId || searchResultItem.id || '';
      if (!offerId) return makeBasic(searchResultItem);
      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ offerId: offerId.toString(), country: 'en' })
      });
      if (!response.ok) return makeBasic(searchResultItem);
      const resData = await response.json();
      const productData = resData.result?.result || resData.product || resData.data || resData;
      let skuInfos = [];
      for (const key of ['productSkuInfos', 'skuInfos', 'skuList', 'skus', 'variants']) {
        if (Array.isArray(productData[key]) && productData[key].length > 0) { skuInfos = productData[key]; break; }
      }
      if (!skuInfos.length && Array.isArray(productData?.productInfo?.skuInfos)) skuInfos = productData.productInfo.skuInfos;
      if (!skuInfos.length && Array.isArray(productData?.data?.productSkuInfos)) skuInfos = productData.data.productSkuInfos;
      let minRmbPrice = getOnePieceRmbPrice(searchResultItem, 0);
      if (skuInfos.length > 0) {
        const prices = skuInfos.map(sku => getOnePieceRmbPrice(sku, 0)).filter(p => p > 0);
        if (prices.length > 0) minRmbPrice = Math.min(...prices);
      }
      const minWeight = parseFloat(searchResultItem.weight || searchResultItem.productWeight || 0.5);
      const priceData = await calculateINRPrice(minRmbPrice, offerId, '', minWeight, null);
      return {
        id: offerId, offerId,
        title: searchResultItem.subjectTrans || searchResultItem.subject || searchResultItem.title || 'Product',
        image: searchResultItem.imageUrl || searchResultItem.image || '',
        price: { rmb: minRmbPrice, inr: priceData.inr, breakdown: priceData.breakdown },
        variantCount: skuInfos.length || 1,
        supplier: searchResultItem.companyName || searchResultItem.supplier || 'Supplier',
        monthSold: searchResultItem.monthSold || 0,
        moq: searchResultItem.moq || searchResultItem.minOrderQuantity || 1,
        rating: searchResultItem.sellerRating || searchResultItem.rating || null,
      };
    } catch (err) {
      return makeBasic(searchResultItem);
    }
  }, [calculateINRPrice]);

  const compressImage = useCallback(async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob((b) => resolve(b || blob), 'image/jpeg', 0.7);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
      img.src = url;
    });
  }, []);

  const handleFindSimilar = useCallback(async (page = 1) => {
    const allImages = data?.images?.length ? data.images : (data?.mainImage ? [data.mainImage] : []);
    const imageUrl = allImages[activeImageIdx] || allImages[0];
    if (!imageUrl) { alert('No image available'); return; }
    setLoadingSimilar(true);
    setFindSimilarProgress(10);
    setFindSimilarStatus('Preparing image...');
    setShowSimilar(true);
    setCurrentPage(page);
    try {
      setFindSimilarProgress(25);
      setFindSimilarStatus('Fetching source image...');
      const proxyUrl = `${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResponse = await fetch(proxyUrl);
      if (!imgResponse.ok) throw new Error('Failed to fetch image');
      let imgBlob = await imgResponse.blob();
      imgBlob = await compressImage(imgBlob);
      setFindSimilarProgress(45);
      setFindSimilarStatus('Compressing image...');
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      setFindSimilarProgress(60);
      setFindSimilarStatus('Searching similar products...');
      const response = await fetch(baseUrl + '/get-product-info-by-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ imageBase64: base64Image, beginPage: page, pageSize: 6, country: 'en' }),
      });
      if (!response.ok) throw new Error('API error');
      const result = await response.json();
      const totalPagesFromApi = result.pagination?.totalPage || result.totalPages || 5;
      setTotalPages(totalPagesFromApi);
      let productsData = result.products || result.result?.result?.data || result.data || [];
      if (Array.isArray(productsData) && productsData.length > 0) {
        setFindSimilarProgress(75);
        setFindSimilarStatus('Fetching product details and variants...');
        const processed = await Promise.all(
          productsData.slice(0, 6).map((item) => fetchProductDetailsWithVariants(item))
        );
        setFindSimilarProgress(100);
        setFindSimilarStatus('Similar products ready');
        setSimilarProducts(processed);
      } else {
        setFindSimilarProgress(100);
        setFindSimilarStatus('No products found');
        setSimilarProducts([]);
      }
    } catch (err) {
      console.error('Find similar error:', err);
      setSimilarProducts([]);
      setFindSimilarStatus('Error: ' + err.message);
    } finally {
      setLoadingSimilar(false);
    }
  }, [data, activeImageIdx, compressImage, fetchProductDetailsWithVariants]);

  // Open product variant detail page inline in sidebar
  const openProductVariants = useCallback(async (product) => {
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setLoadingSelectedProduct(true);
    setShowSimilar(false);
    setSelectedVariantImageIdx(0);

    const selectedInfo = {
      offerId: product.offerId,
      title: product.title,
      image: product.image,
      rmb: product.price.rmb,
      inr: product.price.inr,
    };

    try {
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl + '/product-detail-by-offerid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Token': token },
        body: JSON.stringify({ offerId: product.offerId.toString(), country: 'en' })
      });
      if (!response.ok) throw new Error('Failed to fetch product details');
      const data = await response.json();
      const productData = data.result?.result || data.product || data.data || data;
      productData._selectedInfo = selectedInfo;
      setSelectedProductDetails(productData);

      let skuInfos = productData.productSkuInfos || productData.skuInfos || productData.skuList ||
        productData.skus || productData.productInfo?.skuInfos || productData.data?.productSkuInfos ||
        productData.variants || [];

      const shippingDetails = productData.productShippingInfo?.skuShippingDetails || [];

      if (skuInfos.length > 0) {
        const processedVariants = await Promise.all(
          skuInfos.map(async (sku, idx) => {
            const skuId = sku.skuId || sku.id || sku.specId || `sku-${idx}`;
            const skuPrice = getOnePieceRmbPrice(sku, selectedInfo.rmb);
            const skuShipping = shippingDetails.find(s => s.skuId == skuId) || shippingDetails[0] || {};
            const weight = parseFloat(skuShipping.weight || 0);
            const length = parseFloat(skuShipping.length || 0);
            const width = parseFloat(skuShipping.width || 0);
            const height = parseFloat(skuShipping.height || 0);
            const dimensions = (length > 0 && width > 0 && height > 0) ? { length, width, height } : null;
            const priceData = await calculateINRPrice(skuPrice, product.offerId, skuId, weight, dimensions);

            let attributes = [];
            let attrImageUrl = null;
            if (sku.skuAttributes?.length > 0) {
              attributes = sku.skuAttributes.map(attr => {
                if (!attrImageUrl && attr.skuImageUrl) attrImageUrl = attr.skuImageUrl;
                return { name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute', value: attr.valueTrans || attr.value || attr.attributeValue || '', imageUrl: attr.skuImageUrl || null };
              });
            } else if (sku.attributes?.length > 0) {
              attributes = sku.attributes.map(attr => {
                if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) attrImageUrl = attr.skuImageUrl || attr.imageUrl;
                return { name: attr.name || attr.attributeName || 'Attribute', value: attr.value || attr.attributeValue || '', imageUrl: attr.skuImageUrl || attr.imageUrl || null };
              });
            }

            const variantImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || selectedInfo.image;

            return {
              id: skuId, skuId,
              image: variantImage,
              price: { rmb: skuPrice, inr: priceData.inr || 0, breakdown: priceData.breakdown },
              stock: sku.amountOnSale || sku.stock || sku.canBookCount || 0,
              attributes,
              attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`,
              weight, dimensions: priceData.dimensions || dimensions,
              volumetricWeight: priceData.volumetricWeight || 0,
            };
          })
        );
        setSelectedProductVariants(processedVariants);
      }
    } catch (err) {
      console.error('Failed to fetch selected product details:', err);
      setSelectedProductDetails({ _selectedInfo: selectedInfo });
    } finally {
      setLoadingSelectedProduct(false);
    }
  }, [calculateINRPrice]);

  const backToSimilarProducts = () => {
    setSelectedProductDetails(null);
    setSelectedProductVariants([]);
    setShowSimilar(true);
  };

  const fetchProductData = useCallback(async () => {
    if (!urlFromQuery) { setError("Missing Flipkart URL"); setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      const response = await fetch(`${BACKEND_API}order/flipkart-product?url=${encodeURIComponent(urlFromQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = await response.json();
      if (res?.success && res?.data) {
        setData(res.data);
        if (res.data.error) setError(res.data.error);
      } else {
        setError(res?.message || "Failed to fetch product details");
      }
    } catch (err) {
      setError(err?.message || "Failed to fetch product details");
    } finally {
      setLoading(false);
    }
  }, [urlFromQuery]);

  useEffect(() => { fetchProductData(); }, [fetchProductData]);

  if (loading) {
    return (
      <div className="apd-container apd-loading-container">
        <div className="apd-loading">
          <FaSpinner className="apd-spinner" />
          <p>Loading Flipkart product...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="apd-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 16 }}>
          <p style={{ color: '#ef4444', fontWeight: 600 }}>{error}</p>
          <button onClick={fetchProductData} style={{ padding: '8px 20px', background: '#2874F0', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            <FaSync style={{ marginRight: 6 }} /> Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const images = data.images?.length ? data.images : (data.mainImage ? [data.mainImage] : []);
  const hasVariations = data.variations && (data.variations.colors?.length || data.variations.sizes?.length || data.variations.styles?.length);

  return (
    <div className={`apd-container${showSimilar || selectedProductDetails ? ' apd-with-sidebar' : ''}`}>
      <div className="apd-main">
        <div className="apd-header">
          <div className="apd-header-left">
            <span style={{ background: '#2874F0', color: '#fff', padding: '4px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>Flipkart</span>
            {data.productId && <span className="apd-asin-badge">{data.productId}</span>}
          </div>
          <div className="apd-header-right">
            {data.url && (
              <a href={data.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: '#2874F0', color: '#fff', borderRadius: 4, fontSize: 10, fontWeight: 600, textDecoration: 'none' }}>
                Flipkart <FaExternalLinkAlt size={9} />
              </a>
            )}
            <button onClick={fetchProductData} disabled={loading} className="apd-btn-reload" title="Refresh">
              <FaSync size={10} /> Refresh
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className="apd-product-layout">
          <div className="apd-images-section">
            <div style={{ position: 'relative', width: '100%', aspectRatio: '1', background: '#f8fafc', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <img src={images[activeImageIdx] || noImage} alt={data.title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.src = noImage; }} />
              <button className="apd-find-similar-icon" onClick={() => handleFindSimilar(1)} disabled={loadingSimilar} title="Find Similar Products">
                {loadingSimilar ? <FaSpinner className="apd-spinner" /> : <TbZoomScan size={20} />}
              </button>
            </div>
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                {images.slice(0, 8).map((img, i) => (
                  <div key={i} onClick={() => setActiveImageIdx(i)} style={{ width: 48, height: 48, borderRadius: 6, overflow: 'hidden', border: i === activeImageIdx ? '2px solid #2874F0' : '1px solid #e2e8f0', cursor: 'pointer', background: '#f8fafc', flexShrink: 0 }}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = noImage; }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ flex: 1 }}>
            {data.brand && <p style={{ color: '#2874F0', fontSize: 13, fontWeight: 600, margin: '0 0 4px 0' }}>{data.brand}</p>}
            <h2 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 600, color: '#0f172a', lineHeight: 1.4 }}>{data.title || 'Flipkart Product'}</h2>
            {data.rating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#388e3c', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                  {data.rating} <FaStar size={10} />
                </span>
                {data.ratingCount && <span style={{ color: '#878787', fontSize: 13 }}>{data.ratingCount}</span>}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              {data.price && <span style={{ fontSize: 24, fontWeight: 700, color: '#212121' }}>{data.price}</span>}
              {data.mrp && data.mrp !== data.price && <span style={{ fontSize: 15, color: '#878787', textDecoration: 'line-through' }}>{data.mrp}</span>}
              {data.discount && <span style={{ fontSize: 14, fontWeight: 600, color: '#388e3c' }}>{data.discount}</span>}
            </div>
            {data.dealBadge && (
              <div style={{ background: '#ff6161', color: '#fff', padding: '4px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, display: 'inline-block', marginBottom: 10 }}>{data.dealBadge}</div>
            )}
            {data.availability && (
              <p style={{ fontSize: 13, color: data.availability.toLowerCase().includes('unavailable') ? '#ef4444' : '#388e3c', fontWeight: 600, margin: '0 0 8px 0' }}>{data.availability}</p>
            )}
            {data.seller && (
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FaStore size={12} />
                <span>Sold by: <strong style={{ color: '#2874F0' }}>{data.seller}</strong></span>
                {data.sellerRating && <span>({data.sellerRating})</span>}
              </div>
            )}
            {data.url && (
              <a href={data.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#2874F0', textDecoration: 'none', marginBottom: 8 }}>
                View on Flipkart <FaExternalLinkAlt size={10} />
              </a>
            )}
          </div>
        </div>

        {hasVariations && (
          <div className="alibaba-section" style={{ marginTop: 16 }}>
            <div className="alibaba-section-header"><h3 className="alibaba-section-title"><FaBox size={13} /> Variants</h3></div>
            <div className="alibaba-section-content">
              {data.variations.colors?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Colors:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.variations.colors.map((c, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 12, background: c.selected ? '#e3f2fd' : '#f1f5f9', border: c.selected ? '1px solid #2874F0' : '1px solid #e2e8f0', color: c.selected ? '#2874F0' : '#475569' }}>{c.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {data.variations.sizes?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Sizes:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.variations.sizes.map((s, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 12, background: s.selected ? '#e3f2fd' : '#f1f5f9', border: s.selected ? '1px solid #2874F0' : '1px solid #e2e8f0', color: s.selected ? '#2874F0' : '#475569' }}>{s.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {data.variations.styles?.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Styles:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {data.variations.styles.map((st, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 12, background: st.selected ? '#e3f2fd' : '#f1f5f9', border: st.selected ? '1px solid #2874F0' : '1px solid #e2e8f0', color: st.selected ? '#2874F0' : '#475569' }}>{st.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {data.offers?.length > 0 && (
          <div className="alibaba-section" style={{ marginTop: 16 }}>
            <div className="alibaba-section-header"><h3 className="alibaba-section-title">Available Offers</h3></div>
            <div className="alibaba-section-content">
              {data.offers.map((offer, i) => (
                <p key={i} style={{ fontSize: 12, color: '#475569', margin: '0 0 6px 0', paddingLeft: 10, borderLeft: '2px solid #2874F0' }}>{offer}</p>
              ))}
            </div>
          </div>
        )}

        {data.deliveryInfo?.length > 0 && (
          <div className="alibaba-section" style={{ marginTop: 16 }}>
            <div className="alibaba-section-header"><h3 className="alibaba-section-title">Delivery Information</h3></div>
            <div className="alibaba-section-content">
              {data.deliveryInfo.map((info, i) => (
                <p key={i} style={{ fontSize: 12, color: '#475569', margin: '0 0 4px 0' }}>{info}</p>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: 16 }}>
            {['description', 'highlights', 'specifications', 'reviews'].map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                color: activeTab === tab ? '#2874F0' : '#94a3b8',
                borderBottom: activeTab === tab ? '2px solid #2874F0' : '2px solid transparent',
                marginBottom: -2
              }}>{tab}</button>
            ))}
          </div>

          {activeTab === 'description' && (
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7 }}>{data.description || 'No description available.'}</div>
          )}

          {activeTab === 'highlights' && (
            <div>
              {data.features?.length > 0 ? (
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {data.features.map((f, i) => (
                    <li key={i} style={{ fontSize: 13, color: '#475569', marginBottom: 6, lineHeight: 1.5 }}>{f}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>No highlights available.</p>
              )}
            </div>
          )}

          {activeTab === 'specifications' && (
            <div>
              {Object.keys(data.productDetails || {}).length > 0 ? (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.entries(data.productDetails).map(([key, value], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 12px', color: '#64748b', fontWeight: 500, width: '40%', verticalAlign: 'top' }}>{key}</td>
                        <td style={{ padding: '8px 12px', color: '#0f172a' }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>No specifications available.</p>
              )}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              {data.topReviews?.length > 0 ? (
                data.topReviews.map((review, i) => (
                  <div key={i} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      {review.rating && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#388e3c', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                          {review.rating} <FaStar size={9} />
                        </span>
                      )}
                      {review.title && <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{review.title}</span>}
                    </div>
                    <p style={{ fontSize: 13, color: '#475569', margin: '0 0 4px 0', lineHeight: 1.5 }}>{review.text}</p>
                    {review.date && <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{review.date}</p>}
                  </div>
                ))
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>No reviews available.</p>
              )}
            </div>
          )}
        </div>

        {data.categories?.length > 0 && (
          <div className="alibaba-section" style={{ marginTop: 16 }}>
            <div className="alibaba-section-header"><h3 className="alibaba-section-title">Categories</h3></div>
            <div className="alibaba-section-content">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.categories.map((cat, i) => (
                  <span key={i} style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>{cat}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {images.length > 1 && (
          <div className="alibaba-section" style={{ marginTop: 16 }}>
            <div className="alibaba-section-header"><h3 className="alibaba-section-title">Product Images ({images.length})</h3></div>
            <div className="alibaba-section-content">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {images.map((img, i) => (
                  <div key={i} style={{ aspectRatio: '1', borderRadius: 6, overflow: 'hidden', border: '1px solid #e2e8f0', cursor: 'pointer', background: '#f8fafc' }} onClick={() => setActiveImageIdx(i)}>
                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = noImage; }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {(showSimilar || selectedProductDetails) && (
        <div className="apd-sidebar">
          {selectedProductDetails ? (
            <>
              <div className="apd-sidebar-header">
                <button onClick={backToSimilarProducts} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#3b82f6', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0 }}>
                  {"\u2190"} Back to Similar Products
                </button>
                <button className="apd-sidebar-close" onClick={backToSimilarProducts} title="Back to similar products">x</button>
              </div>

              {loadingSelectedProduct ? (
                <div className="apd-sidebar-loading">
                  <FaSpinner className="apd-spinner" />
                  <p>Loading product details...</p>
                </div>
              ) : (
                <div className="apd-detail-scroll">
                  {/* Two Column Layout - Image Left, Info Right */}
                  <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px', padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        {(() => {
                          const productImages = selectedProductDetails.productImage?.images || selectedProductDetails.images || selectedProductDetails.imageList || selectedProductDetails.productInfo?.images || [];
                          const selectedInfo = selectedProductDetails._selectedInfo || {};
                          const selectedVariant = selectedProductVariants[selectedVariantImageIdx];
                          const mainImg = selectedVariant?.image || productImages[0] || selectedInfo.image || noImage;
                          return <img src={mainImg} alt={selectedProductDetails.subjectTrans || selectedInfo.title || 'Product'} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }} onError={(e) => { e.currentTarget.src = noImage; }} />;
                        })()}
                      </div>
                      {(() => {
                        const variantImages = selectedProductVariants.map(v => v.image).filter((img, idx, arr) => img && arr.indexOf(img) === idx);
                        const productImages = selectedProductDetails.productImage?.images || selectedProductDetails.images || selectedProductDetails.imageList || selectedProductDetails.productInfo?.images || [];
                        const thumbnailImages = variantImages.length > 0 ? variantImages : productImages;
                        return thumbnailImages.length > 1 && (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {thumbnailImages.slice(0, 5).map((img, idx) => {
                              const variantIdx = selectedProductVariants.findIndex(v => v.image === img);
                              const isActive = variantIdx >= 0 ? variantIdx === selectedVariantImageIdx : idx === selectedVariantImageIdx;
                              return <img key={idx} src={img} alt={`Thumb ${idx + 1}`} style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px', border: isActive ? '2px solid #3b82f6' : '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => { if (variantIdx >= 0) setSelectedVariantImageIdx(variantIdx); else setSelectedVariantImageIdx(idx); }} onError={(e) => { e.currentTarget.src = noImage; }} />;
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '12px', lineHeight: 1.4 }}>
                        {selectedProductDetails.subjectTrans || selectedProductDetails.subject || selectedProductDetails._selectedInfo?.title || 'Product'}
                      </h2>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px' }}>Estimated Landed Price</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b' }}>
                          {"\u20B9"}{selectedProductVariants[selectedVariantImageIdx]?.price?.inr?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          MOQ: {selectedProductDetails.productSaleInfo?.minOrderQuantity || selectedProductDetails.moq || 1} pieces
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Offer ID</div>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{selectedProductDetails._selectedInfo?.offerId || 'N/A'}</div>
                        </div>
                        <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                          <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Unit</div>
                          <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{selectedProductDetails.productSaleInfo?.unit || 'piece'}</div>
                        </div>
                        {selectedProductDetails.productSaleInfo?.amountOnSale && (
                          <div style={{ padding: '8px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                            <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Stock</div>
                            <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b' }}>{selectedProductDetails.productSaleInfo.amountOnSale}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Available Variants */}
                  <div style={{ padding: '0 16px 16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0', marginTop: '16px' }}>
                      Available Variants ({selectedProductVariants.length})
                    </h4>
                    {selectedProductVariants.length > 0 ? (
                      <div className="apd-variants-scroll">
                        {selectedProductVariants.map((variant, idx) => (
                          <div key={variant.id} onClick={() => setSelectedVariantImageIdx(idx)} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 12px', backgroundColor: idx === selectedVariantImageIdx ? '#eff6ff' : '#fff', border: idx === selectedVariantImageIdx ? '2px solid #3b82f6' : '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer' }}>
                            <img src={variant.image || selectedProductDetails._selectedInfo?.image || noImage} alt={variant.attributeText} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} onError={(e) => { e.currentTarget.src = noImage; }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 500, color: '#1e293b', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{variant.attributeText}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '15px', fontWeight: 600, color: '#ea580c' }}>{"\u20B9"}{variant.price.inr.toFixed(0)}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                {variant.weight > 0 && <span style={{ fontSize: '9px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 5px', borderRadius: '3px' }}>{variant.weight.toFixed(2)}kg</span>}
                                {variant.dimensions && variant.dimensions.length > 0 && <span style={{ fontSize: '9px', backgroundColor: '#e0e7ff', color: '#3730a3', padding: '2px 5px', borderRadius: '3px' }}>{variant.dimensions.length}{"\u00D7"}{variant.dimensions.width}{"\u00D7"}{variant.dimensions.height}cm</span>}
                                {variant.stock > 0 && <span style={{ fontSize: '9px', color: '#10b981', backgroundColor: '#dcfce7', padding: '2px 5px', borderRadius: '3px' }}>Stock: {variant.stock}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>No variant information available</div>
                    )}
                  </div>

                  {/* Product Sale Info */}
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header"><div className="alibaba-section-title">Product Sale Info</div></div>
                    <div className="alibaba-section-content">
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <tbody>
                            {Object.entries(selectedProductDetails.productSaleInfo || {}).filter(([key]) => key !== 'priceRangeList').map(([key, value], idx) => {
                              let displayValue = Array.isArray(value) ? (value.length > 0 ? value.join(', ') : '-') : typeof value === 'object' && value !== null ? Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ') : String(value);
                              return (<tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}><td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px', width: '140px' }}>{key}</td><td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>{displayValue}</td></tr>);
                            })}
                          </tbody>
                        </table>
                      </div>
                      {(!selectedProductDetails.productSaleInfo || Object.keys(selectedProductDetails.productSaleInfo).length === 0) && <div style={{ color: '#94a3b8', fontSize: '12px', padding: '12px', textAlign: 'center' }}>No sale info available</div>}
                    </div>
                  </div>

                  {/* Shipping Info */}
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header"><div className="alibaba-section-title">Shipping Info</div></div>
                    <div className="alibaba-section-content">
                      {(() => {
                        const skuShippingDetails = selectedProductDetails.productShippingInfo?.skuShippingDetails || [];
                        return skuShippingDetails.length > 0 ? (
                          <div style={{ overflowX: 'auto', border: '1px solid #bae6fd', borderRadius: '8px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                              <thead><tr style={{ background: '#f0f9ff' }}>
                                <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>SKU ID</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Weight (kg)</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>L {"\u00D7"} W {"\u00D7"} H (cm)</th>
                                <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600 }}>Spec ID</th>
                              </tr></thead>
                              <tbody>
                                {skuShippingDetails.slice(0, 20).map((sku, idx) => (
                                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', color: '#1e293b' }}>{sku.skuId || '-'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b', fontWeight: 500 }}>{sku.weight || '-'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#1e293b' }}>{sku.length && sku.width && sku.height ? `${sku.length} ${"\u00D7"} ${sku.width} ${"\u00D7"} ${sku.height}` : '-'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontFamily: 'monospace', fontSize: '10px' }}>{sku.specId || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : <div style={{ color: '#94a3b8', fontSize: '12px' }}>No shipping info available</div>;
                      })()}
                    </div>
                  </div>

                  {/* Product Attributes */}
                  {(selectedProductDetails.productAttribute || []).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header"><div className="alibaba-section-title">Product Attributes ({(selectedProductDetails.productAttribute || []).length})</div></div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {(selectedProductDetails.productAttribute || []).map((attr, idx) => (
                            <div key={idx} style={{ display: 'flex', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', minWidth: '100px', flexShrink: 0 }}>{attr.attributeNameTrans || attr.attributeName || attr.name || `Attr ${idx + 1}`}</span>
                              <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b', wordBreak: 'break-word' }}>{attr.valueTrans || attr.value || attr.attributeValue || 'N/A'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Product Images Gallery */}
                  {selectedProductDetails.productImage && (selectedProductDetails.productImage.images || []).length > 0 && (
                    <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                      <div className="alibaba-section-header"><div className="alibaba-section-title">Product Images ({(selectedProductDetails.productImage?.images || []).length})</div></div>
                      <div className="alibaba-section-content">
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {(selectedProductDetails.productImage?.images || []).map((img, idx) => (
                            <div key={idx} style={{ width: '70px', height: '70px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f8fafc', cursor: 'pointer' }} onClick={() => window.open(img, '_blank')} title={`Image ${idx + 1} - Click to open`}>
                              <img src={img} alt={`Product ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Other Product Data */}
                  <div className="alibaba-section" style={{ marginLeft: 0, marginRight: 0 }}>
                    <div className="alibaba-section-header">
                      <div className="alibaba-section-title">{"\uD83D\uDCDD"} Other Product Data</div>
                    </div>
                    <div className="alibaba-section-content">
                      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600, width: '180px' }}>Field</th>
                              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 600 }}>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedProductDetails)
                              .filter(([key]) => !['productSaleInfo', 'productShippingInfo', 'sellerDataInfo', 'productAttribute', 'tagInfoList', 'productFeature', 'productImage', 'productSkuInfos', 'skuInfos', '_selectedInfo', 'variations', 'promotionUrl', 'subject', 'companyName'].includes(key))
                              .filter(([, value]) => value !== null && value !== undefined && value !== '')
                              .map(([key, value], idx) => (
                                <tr key={key} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#64748b', fontWeight: 500, textTransform: 'uppercase', fontSize: '10px' }}>
                                    {key}
                                  </td>
                                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#1e293b', wordBreak: 'break-all' }}>
                                    {typeof value === 'object'
                                      ? (Array.isArray(value) ? `[${value.length} items]` : JSON.stringify(value).substring(0, 300))
                                      : String(value).substring(0, 300)}
                                    {(typeof value === 'string' && value.length > 300) && '...'}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </>
          ) : (
            <>
          <div className="apd-sidebar-header">
            <h3>Similar Products from BTPL/Global</h3>
            <button className="apd-sidebar-close" onClick={() => setShowSimilar(false)}>x</button>
          </div>

          {similarProducts.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{similarProducts.length} similar products found</span>
                <select value={sortOption} onChange={(e) => setSortOption(e.target.value)} style={{ padding: '4px 8px', fontSize: 11, fontWeight: 500, border: '1px solid #e2e8f0', borderRadius: 4, backgroundColor: '#fff', color: '#475569', cursor: 'pointer', outline: 'none' }}>
                  <option value="default">Sort: Default</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => handleFindSimilar(currentPage - 1)} disabled={currentPage <= 1 || loadingSimilar} style={{ padding: '6px 12px', backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6', color: currentPage <= 1 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: currentPage <= 1 ? 'not-allowed' : 'pointer' }}>Prev</button>
                <span style={{ fontSize: 12, color: '#64748b' }}>{currentPage} / {totalPages}</span>
                <button onClick={() => handleFindSimilar(currentPage + 1)} disabled={currentPage >= totalPages || loadingSimilar} style={{ padding: '6px 12px', backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6', color: currentPage >= totalPages ? '#94a3b8' : '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
              </div>
            </div>
          )}

          {loadingSimilar ? (
            <div className="apd-sidebar-loading">
              <FaSpinner className="apd-spinner" />
              <p>{findSimilarStatus || 'Searching on BTPL/Global...'}</p>
              <div className="apd-find-progress-wrap">
                <div className="apd-find-progress-bar" style={{ width: `${findSimilarProgress}%` }} />
              </div>
              <span className="apd-find-progress-text">{findSimilarProgress}%</span>
            </div>
          ) : similarProducts.length > 0 ? (
            <>
              <div className="apd-similar-grid">
                {sortedSimilarProducts.slice(0, 6).map((product, index) => {
                  const matchPercent = sortOption === 'default' ? Math.max(95 - (index * 3), 45) : null;
                  return (
                    <div key={product.id} className="apd-pcard" onClick={() => openProductVariants(product)} style={{ cursor: 'pointer' }}>
                      <div className="apd-pcard-img-wrap">
                        <img src={product.image} alt={product.title} className="apd-pcard-img" onError={(e) => { e.currentTarget.src = noImage; }} />
                        {matchPercent !== null && (
                          <div style={{ position: 'absolute', top: 6, right: 6, padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, backgroundColor: matchPercent >= 80 ? '#dcfce7' : matchPercent >= 60 ? '#fef3c7' : '#fee2e2', color: matchPercent >= 80 ? '#16a34a' : matchPercent >= 60 ? '#d97706' : '#dc2626', border: `1px solid ${matchPercent >= 80 ? '#bbf7d0' : matchPercent >= 60 ? '#fcd34d' : '#fecaca'}`, zIndex: 2 }}>
                            {matchPercent}%
                          </div>
                        )}
                      </div>
                      <div className="apd-pcard-variants">{product.variantCount || 1} Variants</div>
                      <div className="apd-pcard-title" title={product.title}>{product.title}</div>
                      <div className="apd-pcard-prices">
                        <span className="apd-pcard-price-inr">{'\u20B9'}{(product.price?.inr || 0).toFixed(0)}</span>
                      </div>
                      <div className="apd-pcard-meta">
                        <span className="apd-pcard-moq">MOQ: {product.moq || 1}</span>
                        <span className="apd-pcard-rating"><FaStar /> {product.rating || (Math.random() * 1.5 + 3.5).toFixed(1)}</span>
                      </div>
                      <div className="apd-pcard-actions">
                        <button className="apd-pcard-btn" onClick={(e) => { e.stopPropagation(); openProductVariants(product); }}>View Variants</button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '12px 16px', backgroundColor: '#fff', borderTop: '1px solid #e2e8f0', gap: 12 }}>
                <button onClick={() => handleFindSimilar(currentPage - 1)} disabled={currentPage <= 1 || loadingSimilar} style={{ padding: '8px 16px', backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#3b82f6', color: currentPage <= 1 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: currentPage <= 1 ? 'not-allowed' : 'pointer' }}>{'\u2190'} Previous</button>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                <button onClick={() => handleFindSimilar(currentPage + 1)} disabled={currentPage >= totalPages || loadingSimilar} style={{ padding: '8px 16px', backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#3b82f6', color: currentPage >= totalPages ? '#94a3b8' : '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}>Next {'\u2192'}</button>
              </div>
            </>
          ) : (
            <div className="apd-sidebar-empty"><p>No similar products found</p></div>
          )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
